import { Extension } from '@tiptap/core';
import type { Node } from '@tiptap/pm/model';
import type { Selection, SelectionRange, Transaction } from '@tiptap/pm/state';

import '@tiptap/extension-text-style';

import { removeEmptyTextStyle, selectionCoversTextblock } from './helpers';
import { setMarkerOnFullySelectedItems } from './ListItem';

type FontSizeOptions = {
  types: string[];
  getStyle: (fontSize: string) => string;
};

const setFontSizeOnCoveredParagraphs = (tr: Transaction, selection: Selection, fontSize: string | null): void => {
  const paragraphType = tr.doc.type.schema.nodes.paragraph;
  const { storedMarks } = tr;

  selection.ranges.forEach((range: SelectionRange) => {
    tr.doc.nodesBetween(range.$from.pos, range.$to.pos, (node: Node, pos: number) => {
      if (node.type !== paragraphType) return undefined;

      if (selectionCoversTextblock(range, node, pos) && node.attrs.fontSize !== fontSize) {
        tr.setNodeAttribute(pos, 'fontSize', fontSize);
      }
      return false;
    });
  });
  tr.setStoredMarks(storedMarks);
};

export const FontSize = Extension.create<FontSizeOptions>({
  name: 'fontSize',

  addOptions(): FontSizeOptions {
    return {
      types: ['textStyle'],
      // don't forget to pass size with unit (15px, 1rem, 2em, etc.)
      getStyle: (fontSize: string) => {
        return `font-size: ${fontSize}`;
      },
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element: HTMLElement) => element.style.fontSize.replace(/['"]+/g, ''),
            renderHTML: (attributes: { fontSize?: string | null }) => {
              if (!attributes.fontSize) {
                return {};
              }

              return {
                style: this.options.getStyle(attributes.fontSize),
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain, tr, state, dispatch }: any) => {
          const { selection } = state;
          const applied = chain().setMark('textStyle', { fontSize }).run();
          if (dispatch) {
            setFontSizeOnCoveredParagraphs(tr, selection, fontSize);
            setMarkerOnFullySelectedItems(tr, selection, 'markerSize', fontSize);
          }
          return applied;
        },
      unsetFontSize:
        () =>
        ({ chain, tr, state, dispatch }: any) => {
          const { selection } = state;
          const applied = chain().setMark('textStyle', { fontSize: null }).run();
          if (dispatch) {
            removeEmptyTextStyle(tr, selection);
            setFontSizeOnCoveredParagraphs(tr, selection, null);
            setMarkerOnFullySelectedItems(tr, selection, 'markerSize', null);
          }
          return applied;
        },
    };
  },
});
